import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, TriangleAlert } from 'lucide-react';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { PhoneInput } from '../../../../../components/ui/PhoneInput';
import {
  composePhone,
  isCompletePhone,
  resolvePhoneCountryIso2,
} from '../../../../../utils/phone';
import { createSingleOrder } from '../../../../../features/deliveries/deliveriesThunks';
import { fetchOwnWarehouseOptions } from '../../../../../features/sellers/sellersThunks';
import { COUNTRY_ISO2 } from '../../../../../features/geo/geoConstants';
import {
  AddressFields,
  BLANK_ADDRESS,
  validateAddress,
} from '../../../../../components/address/AddressFields';
import { SHIPPING_METHOD_OPTIONS } from '../../../../../utils/catalogs';
import {
  formatTaxId,
  isValidEmail,
  buildShippingAddress,
} from '../../../../../features/deliveries/orderUploadUtils';
import { DOCUMENT_LABEL_BY_COUNTRY } from '../../../../../features/deliveries/orderUploadConstants';
import { OrderProductsPicker } from '../../../../../components/orders/OrderProductsPicker';
import {
  buildProductsPayload,
  validateOrderProducts,
} from '../../../../../features/deliveries/orderFormProducts';

const BLANK_FORM = {
  shippingMethod: '',
  warehouseId: '',
  referenceId: '',
  recipientName: '',
  recipientLastName: '',
  ...BLANK_ADDRESS,
  notes: '',
  weight: '',
  packages: '',
  taxId: '',
  phoneCountry: '',
  phone: '',
  email: '',
};

/**
 * A blank form pre-set with the calling code of the account's own country,
 * which is the one dialled most of the time.
 *
 * @param {string} country - ISO2 code of the seller's company
 * @returns {object}
 */
const buildEmptyForm = (country) => ({
  ...BLANK_FORM,
  phoneCountry: resolvePhoneCountryIso2(country),
});

const inputClass =
  'w-full bg-ninja-gray/50 dark:bg-[#111] border border-border rounded-xl px-4 py-2.5 text-sm text-ninja-dark dark:text-white outline-none focus:border-ninja-green transition-colors disabled:opacity-60';

const labelClass = 'block text-sm font-semibold text-ninja-dark dark:text-white mb-1.5';

/**
 * Text field with its label and inline validation message.
 */
const Field = ({ label, error, children }) => (
  <div>
    <label className={labelClass}>{label}</label>
    {children}
    {error && <p className="text-danger text-xs mt-1">{error}</p>}
  </div>
);

/**
 * Creates a single order from one of the seller's warehouses.
 *
 * The address section follows the country: Chile is addressed by region and
 * commune alone, while Mexico additionally needs the settlement and the postal
 * code.
 *
 * @param {object} props
 * @param {string} props.country - ISO2 code of the seller's company
 */
export const ManualOrderForm = ({ country }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isSubmittingUpload } = useSelector((state) => state.deliveries);
  const { sellerWarehouseOptions, loadingSellerWarehouseOptions } = useSelector(
    (state) => state.sellers
  );
  const emptyForm = useMemo(() => buildEmptyForm(country), [country]);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [feedback, setFeedback] = useState(null);
  // Picked out of the seller's catalogue; emptied with the seller.
  const [products, setProducts] = useState([]);
  const [productErrors, setProductErrors] = useState({});

  const isMexico = country === COUNTRY_ISO2.MX;
  const documentLabel = DOCUMENT_LABEL_BY_COUNTRY[country] || DOCUMENT_LABEL_BY_COUNTRY.CL;

  useEffect(() => {
    dispatch(fetchOwnWarehouseOptions());
  }, [dispatch]);

  /**
   * @param {string} name
   * @param {string} value
   */
  const setField = (name, value) => {
    setForm((previous) => ({ ...previous, [name]: value }));

    if (errors[name]) {
      setErrors((previous) => ({ ...previous, [name]: null }));
    }
  };

  const handleInput = (event) => setField(event.target.name, event.target.value);

  /** `AddressFields` sends only what changed, cascade included. */
  const handleAddressChange = (next) => {
    setForm((previous) => ({ ...previous, ...next }));
    setErrors((previous) => {
      const cleared = { ...previous };
      Object.keys(next).forEach((key) => { cleared[key] = null; });
      if ('settlement' in next) cleared.settlementId = null;
      return cleared;
    });
  };

  const validate = () => {
    const found = validateAddress(form, { isMexico });

    if (!form.shippingMethod) found.shippingMethod = 'Selecciona el tipo de envío';
    if (!form.warehouseId) found.warehouseId = 'Selecciona la bodega de origen';
    if (!form.referenceId.trim()) found.referenceId = 'Campo obligatorio';
    if (!form.recipientName.trim()) found.recipientName = 'Campo obligatorio';
    if (!form.recipientLastName.trim()) found.recipientLastName = 'Campo obligatorio';
    if (!form.weight || Number(form.weight) <= 0) {
      found.weight = 'Debe ser un número mayor a cero';
    }
    if (!form.packages || !Number.isInteger(Number(form.packages)) || Number(form.packages) <= 0) {
      found.packages = 'Debe ser un entero mayor a cero';
    }
    if (!form.phone.trim()) {
      found.phone = 'Campo obligatorio';
    } else if (!isCompletePhone(form.phone, form.phoneCountry)) {
      found.phone = 'El número está incompleto';
    }

    if (!form.email.trim()) {
      found.email = 'Campo obligatorio';
    } else if (!isValidEmail(form.email)) {
      found.email = 'Formato incorrecto';
    }

    const foundProductErrors = validateOrderProducts(products);
    setProductErrors(foundProductErrors);
    if (Object.keys(foundProductErrors).length > 0) found.products = 'Revisa las cantidades';

    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFeedback(null);

    if (!validate()) {
      setFeedback({ type: 'error', message: 'Revisa los campos marcados en rojo.' });
      return;
    }

    const productsPayload = buildProductsPayload(products);
    const payload = {
      // The backend takes the seller from the session, but it rejects a body
      // without this key before reading it — the legacy sent `null` as well.
      seller_id: null,
      warehouse_id: form.warehouseId,
      reference_id: form.referenceId,
      recipient: {
        name: form.recipientName,
        last_name: form.recipientLastName,
        phone: composePhone(form.phoneCountry, form.phone),
        email: form.email,
        rut: form.taxId,
      },
      shipping_address: buildShippingAddress(form, country),
      delivery_shipping_method: form.shippingMethod,
      packages: form.packages,
      notes: form.notes,
      weight: form.weight,
      // Only when something was picked: without it the order is created as before.
      ...(productsPayload && { products: productsPayload }),
    };

    dispatch(createSingleOrder(payload))
      .unwrap()
      // Straight to where the order now is. The toast that says it was created
      // survives leaving this page.
      .then(() => navigate('/deliveries'))
      .catch((error) => {
        setFeedback({
          type: 'error',
          message: typeof error === 'string' ? error : 'Error al crear la orden.',
        });
      });
  };

  return (
    <form
      data-testid="upload-manual-form"
      onSubmit={handleSubmit}
      className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 sm:p-8 shadow-sm border border-border space-y-6 animate-in fade-in duration-300"
    >
      <div>
        <h3 className="text-lg font-bold text-ninja-dark dark:text-white">
          Carga tu orden mediante Carga Manual
        </h3>
        <p className="text-sm text-text-secondary mt-1">
          Crea una orden individual. El campo Bultos define cuántos paquetes la componen.
        </p>
      </div>

      {/* Origin */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <FilterableSelect
            variant="form"
            testId="upload-manual-shipping-method"
            label="Tipo de Envío"
            value={form.shippingMethod}
            onChange={(value) => setField('shippingMethod', value)}
            options={SHIPPING_METHOD_OPTIONS}
            hasError={Boolean(errors.shippingMethod)}
            searchPlaceholder="Buscar tipo de envío..."
          />
          {errors.shippingMethod && (
            <p className="text-danger text-xs mt-1">{errors.shippingMethod}</p>
          )}
        </div>

        <div>
          <FilterableSelect
            variant="form"
            testId="upload-manual-warehouse"
            label="Bodega de origen"
            value={form.warehouseId}
            onChange={(value) => setField('warehouseId', value)}
            options={sellerWarehouseOptions}
            disabled={loadingSellerWarehouseOptions}
            hasError={Boolean(errors.warehouseId)}
            placeholder={loadingSellerWarehouseOptions ? 'Cargando bodegas...' : 'Seleccionar...'}
            searchPlaceholder="Buscar bodega..."
          />
          {errors.warehouseId && <p className="text-danger text-xs mt-1">{errors.warehouseId}</p>}
        </div>
      </div>

      <div className="h-px bg-border" />

      {/* Products, out of the seller's catalogue */}
      <OrderProductsPicker
        sellerId={null}
        enabled={Boolean(form.warehouseId)}
        value={products}
        onChange={(next) => {
          setProducts(next);
          setProductErrors({});
        }}
        errors={productErrors}
      />

      <div className="h-px bg-border" />

      {/* Recipient */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Id Referencia" error={errors.referenceId}>
          <input
            name="referenceId"
            value={form.referenceId}
            onChange={handleInput}
            className={inputClass}
          />
        </Field>

        <Field label="Nombre Destinatario" error={errors.recipientName}>
          <input
            name="recipientName"
            value={form.recipientName}
            onChange={handleInput}
            className={inputClass}
          />
        </Field>

        <Field label="Apellido Destinatario" error={errors.recipientLastName}>
          <input
            name="recipientLastName"
            value={form.recipientLastName}
            onChange={handleInput}
            className={inputClass}
          />
        </Field>

        {/* The only optional field here, so it says so: everything else in
            this form refuses to submit empty. */}
        <Field label={`${documentLabel} (opcional)`} error={errors.taxId}>
          <input
            name="taxId"
            value={form.taxId}
            onChange={(event) => setField('taxId', formatTaxId(event.target.value, country))}
            placeholder={isMexico ? 'ABCD123456XYZ' : '12.345.678-9'}
            className={inputClass}
          />
        </Field>

        <Field label="Número de contacto" error={errors.phone}>
          <PhoneInput
            value={form.phone}
            countryIso2={form.phoneCountry}
            onChange={(national) => setField('phone', national)}
            onCountryChange={(iso2) => setField('phoneCountry', iso2)}
            hasError={Boolean(errors.phone)}
          />
        </Field>

        <Field label="Correo de contacto" error={errors.email}>
          <input
            name="email"
            type="email"
            value={form.email}
            onChange={handleInput}
            className={inputClass}
          />
        </Field>
      </div>

      <div className="h-px bg-border" />

      {/* Address */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <AddressFields
          country={country}
          value={form}
          onChange={handleAddressChange}
          errors={errors}
        />
      </div>

      <div className="h-px bg-border" />

      {/* Parcel */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Peso (en gramos)" error={errors.weight}>
          <input
            name="weight"
            type="number"
            min="1"
            value={form.weight}
            onChange={handleInput}
            className={inputClass}
          />
        </Field>

        <Field label="Bultos" error={errors.packages}>
          <input
            name="packages"
            type="number"
            min="1"
            value={form.packages}
            onChange={handleInput}
            className={inputClass}
          />
        </Field>

        <div className="sm:col-span-2">
          <Field label="Notas">
            <input name="notes" value={form.notes} onChange={handleInput} className={inputClass} />
          </Field>
        </div>
      </div>

      {feedback && (
        <div
          data-testid="upload-feedback"
          data-type={feedback.type}
          className={
            feedback.type === 'error'
              ? 'flex items-start gap-2 bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm'
              : 'flex items-start gap-2 bg-ninja-greenLight dark:bg-ninja-green/10 text-ninja-green p-3 rounded-xl border border-ninja-green/20 text-sm'
          }
        >
          {feedback.type === 'error' ? (
            <TriangleAlert size={16} className="mt-0.5 shrink-0" />
          ) : (
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          )}
          {feedback.message}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isSubmittingUpload}
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-ninja-green hover:bg-ninja-green/90 text-white font-bold text-sm transition-all disabled:opacity-50"
        >
          {isSubmittingUpload && <Loader2 size={16} className="animate-spin" />}
          {isSubmittingUpload ? 'Creando...' : 'Cargar orden'}
        </button>
      </div>
    </form>
  );
};
